import React, { useState, useEffect, useContext, useRef } from "react";
import { TreeNodeModel, StoreContract } from "../tree/types";
import { AddressForm } from "./AddressForm";
import Message from "../ui/Message";
import { Mcc } from "../../models/interfaces";
import { ApplicationsContext } from "../../context/applications/ApplicationContext";
import { AuthContext } from "../../context/auth";
import { LimitsForm } from "./LimitForm";

import { LimitRow } from "../forms/LimitForm";

interface Props {
  node: TreeNodeModel;
  onSave: (node: TreeNodeModel, store: StoreContract) => void;
  onChange: (node: TreeNodeModel) => void; // ⭐ NUEVO
  readOnly?: boolean;
}

export const CompanyForm: React.FC<Props> = ({ node, onSave, readOnly = false, onChange }) => {
  const { getMCC } = useContext(ApplicationsContext);
  const loadingRef = React.useRef(true);
  const addressRef = useRef<any>(null);
  const { user } = useContext(AuthContext);

  const isUserPayfac = user.groups[0].is_payfac_group == 'Y';

  const STORE_STATUS = [
    {
      code: "00",
      description: "Ok"
    },
    {
      code: "54",
      description: "Para cerrar"
    },
    {
      code: "14",
      description: "Cerrado"
    }
  ];


  const [form, setForm] = useState<StoreContract>({
    id: "",
    divisionContractId: "",
    product: "052-Acq Store level",
    contractName: "",
    mcc: "",
    merchantId: "",
    corporateStatus: "",
    statusCode: "00",
    paymentAddress: {
      addressType: "PAYMENT_SCHEME_ADDRESS",
      country: user?.country?.id || 166,
      provinceId: "",
      cantonCode: "",
      provinceName: "",
      cantonName: "",
      districtName: "",
      districtCode: "",
      addressLine1: "",
      addressLine2: "",
      merchantName: "",
      location: "",
      contact: {
        firstName: "",
        lastName: "",
        mobilePhone: "",
        businessPhone: "",
        email: "",
      },
      postalCode: "",
    },
    classifiers: {
      acqBrandAmexSupport: user.banks[0].code === '0051',
      acqBrandClaveValeSupport: user.banks[0].code === '0052',
      acqBrandMastercardSupport: true,
      acqBrandVisaSupport: true,
      corporateStatus: "NOT_SOLE_PROPRIETOR",
      visaAmmfMerchantRegistration: true,
      amexAmmfMerchantRegistration: user?.country?.code === "CRI" ? true : false,
    },
    status: "DRAFT",

    adjustLimits: "N",
    storeLimits: []
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [mccs, setMccs] = useState<Mcc[]>([]);

  useEffect(() => {
    setTimeout(() => {
      getMCC().then(ms => setMccs(ms));
    }, 50);
  }, []);

  // 🔹 Cargar datos existentes
  useEffect(() => {

    if ((node as any).entityData && node.type === "STORE_CONTRACT") {

      const data = (node as any).entityData as StoreContract;

      setForm(prev => ({
        ...prev,
        ...data,
        paymentAddress: {
          ...prev.paymentAddress,
          ...data.paymentAddress
        },
        classifiers: {
          ...prev.classifiers,
          ...data.classifiers
        }
      }));


    }

    setTimeout(() => {
      loadingRef.current = false;
    }, 0);

  }, [node]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {

    if (readOnly) return;
    if (loadingRef.current) return;

    const { name, value } = e.target;

    let updatedForm: StoreContract;

    if (name === "corporateStatus") {

      updatedForm = {
        ...form,
        classifiers: {
          ...form.classifiers,
          corporateStatus: value as any
        }
      };

    } else if (name === "acqBrandClaveValeSupport") {

      updatedForm = {
        ...form,
        classifiers: {
          ...form.classifiers,
          acqBrandClaveValeSupport: value === "Y"
        }
      };
    }
    else {

      updatedForm = {
        ...form,
        [name]: value
      };

    }

    setForm(updatedForm);

    onChange({
      ...node,
      name: updatedForm.contractName,

      entityData: updatedForm
    });

    if (errorMessage && (name === "contractName" || name === "mcc")) {
      setErrorMessage("");
    }
  };

  const handleSave = () => {
    if (readOnly) return;

    if (form.adjustLimits === "Y") {
      let hasInvalidLimit = false;

      if (!form.storeLimits || form.storeLimits.length === 0) {
        setErrorMessage("Debe configurar al menos un límite.");
        return;
      }

      for (const limit of form.storeLimits) {
        const hasMaxAmount = limit.maxAmount !== undefined && limit.maxAmount !== "";
        const hasMaxSingleAmount = limit.maxSingleAmount !== undefined && limit.maxSingleAmount !== "";
        const hasMaxNumber = limit.maxNumber !== undefined && limit.maxNumber !== "";

        // ❌ valores negativos
        if (hasMaxAmount && Number(limit.maxAmount) < 0) {
          hasInvalidLimit = true;
          break;
        }

        if (hasMaxSingleAmount && Number(limit.maxSingleAmount) < 0) {
          hasInvalidLimit = true;
          break;
        }

        if (hasMaxNumber && Number(limit.maxNumber) < 0) {
          hasInvalidLimit = true;
          break;
        }

        // ❌ ninguno viene lleno
        if (!hasMaxAmount && !hasMaxSingleAmount && !hasMaxNumber) {
          hasInvalidLimit = true;
          break;
        }
      }

      if (hasInvalidLimit) {
        setErrorMessage("Por favor ingrese valores válidos para los límites.");
        return;
      }
    }

    if (!form.contractName.trim() || !form.mcc.trim() || !form.classifiers.corporateStatus.trim()) {
      setErrorMessage("Por favor complete todos los campos requeridos.");
      return;
    }

    const isAddressValid = addressRef.current?.validate();

    if (!isAddressValid) return;



    const updatedNode: TreeNodeModel = {
      ...node,
      name: form.contractName,
      saved: true,
      entityData: form,
    } as any;

    onSave(updatedNode, form);
    setErrorMessage("");
  };

  const removeError = () => setErrorMessage("");

  return (
    <div className="container-fluid">
      <h5 className="mb-3">Empresa / Tienda</h5>

      {/* Producto */}
      <div className="mb-3">
        <label className="form-label">Producto</label>
        <input className="form-control" value="Nivel Tienda" disabled />
      </div>

      {/* Nombre */}
      <div className="row">
        <div className={form.merchantId ? "col-md-6 mb-3" : "col-md-12 mb-3"}>
          <label className="form-label">Nombre de la Tienda *</label>
          <input
            name="contractName"
            className="form-control"
            value={form.contractName}
            onChange={handleChange}
            disabled={readOnly}
            maxLength={38}
          />
        </div>
        {form.merchantId && (
          <div className="col-md-6 mb-3">
            <label className="form-label">Merchant ID</label>
            <input
              className="form-control"
              value={form.merchantId}
              disabled
              placeholder="Asignado automáticamente"
            />
          </div>

        )}
      </div>

      {/* MCC */}
      <div className="mb-3">
        <label className="form-label">Categoría Comercial / MCC *</label>
        <select
          name="mcc"
          className="form-select"
          value={form.mcc}
          onChange={handleChange}
          disabled={readOnly}
        >
          <option value="">Seleccione MCC</option>
          {mccs.map(c => (
            <option key={c.mccId} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </div>



      <div>
        <h6 className="mb-3">Classificadores de tienda</h6>

        <div className="mb-3">
          <label className="form-label">Estado Corporativo *</label>
          <select
            name="corporateStatus"
            className="form-select"
            value={form.classifiers.corporateStatus}
            onChange={handleChange}
            disabled={readOnly}
          >
            <option value="">Seleccione una opción</option>
            <option value="SOLE_PROPRIETOR">
              Sole Proprietor
            </option>
            <option value="NOT_SOLE_PROPRIETOR">
              Not Sole Proprietor
            </option>
          </select>
        </div>

        {user.banks[0].code == '0052' && <div className="mb-3">
          <label className="form-label">Aplica clave *</label>
          <select
            name="acqBrandClaveValeSupport"
            className="form-select"
            value={form.classifiers.acqBrandClaveValeSupport ? 'Y' : 'N'}
            onChange={handleChange}
            disabled={readOnly}
          >
            <option value="">Seleccione una opción</option>
            <option value="Y">
              Sí
            </option>
            <option value="N">
              No
            </option>
          </select>
        </div>}


      </div>


      {node.isExistent && <div className="mb-3">
        <label className="form-label">
          Estado de la Tienda *
        </label>

        <select
          name="statusCode"
          className="form-select"
          value={form.statusCode}
          onChange={handleChange}
          disabled={readOnly || (node.originalEntityData as StoreContract).statusCode === "14"}
        >
          {STORE_STATUS.map(status => (
            <option
              key={status.code}
              value={status.code}
            >
              {status.description}
            </option>
          ))}
        </select>
      </div>}

      {/* Dirección de pagos */}
      <AddressForm
        ref={addressRef}
        title="Dirección"
        addressType="PAYMENT_SCHEME_ADDRESS"
        showPostalCode
        showMerchantName
        data={form.paymentAddress}
        readOnly={readOnly}
        onChange={data => {

          if (loadingRef.current) return;

          const updatedForm = {
            ...form,
            paymentAddress: data
          };

          setForm(updatedForm);

          // ⭐ Actualiza árbol en memoria
          onChange({
            ...node,
            name: updatedForm.contractName,

            entityData: updatedForm
          });

        }}
      />

      {
        user?.isLafise && !isUserPayfac && user?.banks[0].code == '0051' && (
          <>

            <hr />
            <h6 className="mb-3">Límites del comercio</h6>
            <div className="col-md-4 mb-3">
              <label className="form-label">Modificar Límites</label>

              <select
                name="adjustLimits"
                className="form-select"
                value={form.adjustLimits || "N"}
                onChange={handleChange}
                disabled={readOnly}
              >
                <option value="N">No</option>
                <option value="Y">Sí</option>
              </select>
            </div>
            {form.adjustLimits === "Y" && (
              <>


                <LimitsForm
                  limitType="STORE"
                  data={form.storeLimits}
                  readonly={readOnly}
                  onChange={(data) => {

                    const updatedForm = {
                      ...form,
                      storeLimits: data
                    };

                    setForm(updatedForm);
                    if (!onChange) return;

                    onChange({
                      ...node,
                      name: updatedForm.contractName,

                      entityData: updatedForm
                    });
                  }}
                />
              </>
            )}

          </>
        )
      }

      {!readOnly && (
        <button className="btn btn-primary mt-3" onClick={handleSave}>
          Guardar Tienda
        </button>
      )}

      {errorMessage && (
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}
    </div>
  );
};
